import React, {useCallback, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {Width} from 'components/customer/Product/QuickAdd/Width';
import {
    BASE_APPLIED_PANEL,
    getInitialWidth,
    useQuickAddContext,
} from 'components/customer/Product/QuickAdd';
import {Icon} from 'shared/helpers';
import {NumberSpinner} from 'components/customer/Product/QuickAdd/NumberSpinner';
import {Spinner} from 'react-bootstrap';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';

export const Controls = () => {
    const {
        quantity,
        setQuantity,
        loading,
        addProduct,
        item,
        validationMessage,
        onMouseLeave,
        setPopoverOpen,
        resetValidationMessage,
        setWidth,
    } = useQuickAddContext();
    const {room} = useJobContext() as PartialJob;

    const clickHandler = useCallback(() => {
        if (addProduct) {
            void addProduct();
        }
    }, [addProduct]);

    const label = useMemo(() => {
        if (item) {
            if (item.productType == BASE_APPLIED_PANEL) {
                return 'Depth';
            }
        }

        return 'Width';
    }, [item]);

    const handleCancelButton = useCallback(() => {
        setPopoverOpen && setPopoverOpen(false);

        if (onMouseLeave) {
            onMouseLeave();
        }

        if (resetValidationMessage) {
            resetValidationMessage();
        }

        if (item && setWidth) {
            setWidth(getInitialWidth(item, room));
        }

        if (setQuantity) {
            setQuantity(1);
        }
    }, [
        setPopoverOpen,
        resetValidationMessage,
        setWidth,
        item,
        room,
        setQuantity,
    ]);

    return (
        <>
            <InputControl $hasError={validationMessage !== ''}>
                <div>
                    <label htmlFor={`width-${item?.id}`}>{label} (mm)</label>
                    <ControlsContainer>
                        <Width id={`width-${item?.id}`} />
                    </ControlsContainer>
                </div>
                <div>
                    <label htmlFor={`width-${item?.id}`}>Qty</label>
                    <NumberSpinner
                        quantity={quantity}
                        setQuantity={setQuantity}
                        item={item}
                    />
                </div>
            </InputControl>

            {validationMessage ? (
                <Error>
                    <Icon iconName="Error.svg" />
                    {validationMessage}
                </Error>
            ) : null}

            <ButtonContainer>
                <CancelButton
                    disabled={loading ?? false}
                    onClick={handleCancelButton}>
                    Cancel
                </CancelButton>
                <HardwareButtonLocal
                    data-cy="quick-add-button"
                    disabled={loading || validationMessage !== ''}
                    onClick={clickHandler}>
                    {loading ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Icon iconName="Header-Cart.svg" />
                    )}
                    <label>Add</label>
                </HardwareButtonLocal>
            </ButtonContainer>
        </>
    );
};

const Error = styled.div`
    background: #dc3545;
    font-size: 0.8em;
    line-height: 1.2;
    padding: 10px;
    color: white;
    border-radius: 10px;
    font-weight: 400;
    display: flex;
    gap: 10px;

    > img {
        width: 25px;
    }
`;

const ControlsContainer = styled.div`
    display: flex;
    gap: 5px;
`;

const InputControl = styled.div<{$hasError?: boolean}>`
    display: flex;
    flex-direction: row;
    gap: 10px;

    > div:first-child {
        flex-basis: 80%;
    }

    div > label {
        font-size: 0.75em;
        font-weight: 500;
        color: ${({theme, $hasError}) => ($hasError ? '#dc3545' : theme.colors.secondary?.main)};
        text-transform: uppercase;
        margin-bottom: 3px;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const Button = css`
    border-radius: 20px;
    font-weight: 400;
    font-size: 0.85em;
    flex: 1;
    padding: 0px 2px;
`;

const HardwareButtonLocal = styled.button`
    ${Button}
    background: ${({theme}) => theme.colors.secondary?.main};
    color: white;

    &:hover,
    &:focus,
    &:active {
        background: ${({theme}) => theme.colors.primary?.main};
    }

    padding: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 0;

    > label {
        flex: 1;
        cursor: pointer;
    }

    > img {
        width: 20px;
    }

    &:disabled {
        opacity: 0.6;
    }

    > div {
        width: 20px;
        height: 20px;
        border-width: 3px;
    }
`;

const CancelButton = styled.button`
    ${Button}
    border: 2px solid #dfe3e8;
    background: white;
    color: #434343;

    &:hover,
    &:focus,
    &:active {
        border-color: ${({theme}) => theme.colors.primary?.main};
        color: ${({theme}) => theme.colors.primary?.main};
    }
`;
